import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { isDatasetIdentifier } from "utils/utils";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";
import useChartDialogStore from "store/chart-dialog-store";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { T } from "services/lang/LanguageService";
import { MappedValue } from "components/basic/dataset-display-value/DatasetDisplayValue";
import useCopyDataset from "hooks/useCopyDataset";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import useTimerThreshold from "hooks/useTimerThreshold";

type DatasetProps = {
  values: string | string[] | null;
  changeHandlerMulti?: (receivedValue: string[] | null) => void;
  changeHandler?: (receivedValue: string | null) => void;
  isInEditMode: boolean;
  decimalPlaces?: number;
  isSingleDataset?: boolean;
  maxAge?: number;
  updateFrequency?: number;
};

const Dataset: React.FC<DatasetProps> = ({
  changeHandler,
  changeHandlerMulti,
  values,
  isInEditMode,
  decimalPlaces,
  isSingleDataset,
  maxAge,
  updateFrequency,
}) => {
  const getCurrentValues = (): string | string[] | null => {
    if (typeof values === "string") {
      return isSingleDataset ? values : [values];
    }
    return values;
  };

  const [dialogVisible, setDialogVisible] = useState(false);
  const [currentDatasets, setCurrentDatasets] = useState<string | string[] | null>(
    getCurrentValues()
  );
  const handleDatasetsChange = useCallback((newDatasets: string[] | string | null) => {
    setCurrentDatasets(newDatasets);
  }, []);

  const handleDatasetsSave = useCallback(() => {
    if (isSingleDataset && changeHandler) {
      changeHandler(currentDatasets as string | null);
    } else if (changeHandlerMulti) {
      changeHandlerMulti(currentDatasets as string[]);
    }
    setDialogVisible(false);
  }, [changeHandler, changeHandlerMulti, isSingleDataset, currentDatasets]);

  const dialogFooterTemplate = useCallback(() => {
    return (
      <div className="flex justify-content-end gap-2 mt-3">
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={() => setDialogVisible(false)}
          className="component-editor-button icon-red"
        />
        <Button
          type="button"
          label={T("action.save")}
          icon="pi pi-check"
          onClick={handleDatasetsSave}
          className="component-editor-button icon-green"
        />
      </div>
    );
  }, [handleDatasetsSave]);

  if (isInEditMode) {
    const buttonTooltipTemplate = () => {
      if (isSingleDataset) {
        return `${currentDatasets?.length === 0 || currentDatasets == null ? "0" : "1"} ${T("label.selected-datasets")}`;
      }
      return `${currentDatasets?.length} ${T("label.selected-datasets")}`;
    };

    const getLabel = (datasets?: string | string[] | null): string => {
      if (!datasets) return "";

      if (typeof datasets === "string") {
        return datasets.length > 0 ? "1" : "";
      }

      return datasets.length.toString();
    };

    return (
      <>
        <Button
          label={getLabel(currentDatasets)}
          className="bg-gray-200 border-none text-black-alpha-90 hover:bg-gray-300 h-2rem"
          icon="pi pi-list"
          onClick={() => setDialogVisible(true)}
          tooltipOptions={{ position: "bottom" }}
          tooltip={buttonTooltipTemplate()}
        />
        <Dialog
          header={isSingleDataset ? T("action.select-dataset") : T("action.select-datasets")}
          visible={dialogVisible}
          style={{ width: "40rem" }}
          footer={dialogFooterTemplate}
          modal
          onHide={() => setDialogVisible(false)}
        >
          {isSingleDataset ? (
            <DatasetDropdown value={currentDatasets as string} onChange={handleDatasetsChange} />
          ) : (
            <DatasetMultiSelect
              value={currentDatasets as string[]}
              onChange={handleDatasetsChange}
            />
          )}
        </Dialog>
      </>
    );
  }

  const getNormalizedValues = () => {
    if (Array.isArray(values)) {
      return values;
    }
    return values ? [values] : [];
  };

  const datasets = getNormalizedValues().map(value => (isDatasetIdentifier(value) ? value : null));

  if (datasets.length === 0) return "-";

  return (
    <div>
      {datasets?.map(
        dataset =>
          dataset && (
            <DatasetDisplayWrapper
              key={dataset}
              identifier={dataset}
              decimalPlaces={decimalPlaces}
              maxAge={maxAge}
              updateFrequency={updateFrequency}
            />
          )
      )}
    </div>
  );
};

const DatasetDisplayWrapper = ({
  identifier,
  decimalPlaces,
  maxAge,
  updateFrequency,
}: {
  identifier: string;
  decimalPlaces?: number;
  maxAge?: number;
  updateFrequency?: number;
}) => {
  const { data, isLoading } = useGetRecentData([identifier!], {
    options: {
      enabled: !!identifier,
      refetchInterval: updateFrequency ? updateFrequency * 1000 : 0,
    },
  });

  const key = data?.[0].data?.[0]?.key;

  const datasetTime = useMemo(() => (key ? new Date(key as string) : null), [key]);

  if (isLoading) return <div className="pi pi-spin pi-spinner" />;

  const value = data?.[0].data?.[0]?.value;
  const unit = data?.[0].unit;
  const stringValueMapper = data?.[0].stringValueMapper;
  const title = data?.[0].title;

  if (value == null && !isLoading) return <>&#8208;</>;

  return (
    <DatasetDisplay
      identifier={identifier}
      value={value}
      unit={unit}
      title={title}
      stringValueMapper={stringValueMapper}
      decimalPlaces={decimalPlaces}
      time={datasetTime}
      maxAge={maxAge}
    />
  );
};

const DatasetDisplay = ({
  identifier,
  value,
  unit,
  title,
  stringValueMapper,
  decimalPlaces,
  time,
  maxAge,
}: {
  identifier: string;
  value?: number;
  unit?: string;
  title?: string;
  stringValueMapper?: number;
  decimalPlaces?: number;
  time: Date | null;
  maxAge?: number;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  useCopyDataset(isHovered, title, identifier);

  const { isExpired, resetTimer } = useTimerThreshold(maxAge, time?.getTime?.() ?? 0);

  useEffect(() => {
    resetTimer(time?.getTime?.());
  }, [time, resetTimer]);

  const selectedIdsStore = useDatasetIdentifiersStore();
  const { openDialog } = useChartDialogStore();

  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey) {
      if (selectedIdsStore.ids.has(identifier)) {
        selectedIdsStore.removeIdentifier(identifier);
      } else {
        selectedIdsStore.addIdentifier(identifier);
      }
    } else {
      selectedIdsStore.clear();
    }
  };

  const handleDoubleClick = () => {
    selectedIdsStore.addIdentifier(identifier);
    openDialog();
  };

  const isIdentifierSelected = selectedIdsStore.ids.has(identifier);
  return (
    <div
      className="p-inputtext"
      style={{
        border: isIdentifierSelected ? "1px solid #007df2" : "none",
        borderRadius: "0px",
        backgroundColor: "inherit",
        color: "inherit",
        whiteSpace: "pre-wrap",
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      {isExpired && <span className="text-red-500">N/A</span>}
      {!isExpired && stringValueMapper && (
        <MappedValue mapValue={value} mapperId={stringValueMapper} />
      )}

      {!isExpired && !stringValueMapper && (
        <DatasetDisplayValue value={value} unit={unit} decimalPlaces={decimalPlaces} />
      )}
    </div>
  );
};

export default Dataset;

const DatasetDisplayValue = ({
  value,
  unit,
  decimalPlaces,
}: {
  value: number | undefined;
  unit: string | undefined;
  decimalPlaces?: number;
}) => {
  return (
    value && (
      <div style={{ whiteSpace: "nowrap" }}>{`${value.toFixed(decimalPlaces ?? 2)} ${unit}`}</div>
    )
  );
};
